import React, { useState, useCallback } from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as TenantsState from "../../store/Tenants";

import {
  ConnectUserResponse,
  TenantResponse,
  AccessControlGroupResponse,
  SiteSystemType,
} from "../../api/ApiClient";

import SelectTenant from "../shared/SelectTenant";

import Modal from "../shared/Modal";
import SelectClientUser from "../shared/SelectClientUser";

import "./adminSites.css";
import { RegisterAccount } from "./AdminAccounts";
import SelectAccessControlGroup from "../shared/SelectAccessControlGroup";
import { useSnackbar } from "notistack";
import { Button } from "components/ui/button";

type OwnProps = {};

interface Injected {
  systemTypes: SiteSystemType[];
  isLoading: boolean;
}
type Props = Injected & typeof TenantsState.actionCreators & OwnProps;

type ModalType =
  | "tenant"
  | "user"
  | "backendUser"
  | "accessControlGroup"
  | "account"
  | null;

const AdminTenants: React.FC<Props> = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [inProgress, setInProgress] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<TenantResponse | null>(
    null,
  );
  const [showingModal, setShowingModal] = useState<ModalType>(null);
  const [selectedUser, setSelectedUser] = useState<ConnectUserResponse | null>(
    null,
  );
  const [selectingUser, setSelectingUser] = useState(true);
  const [role, setRole] = useState("");
  const [selectedAccessControlGroup, setSelectedAccessControlGroup] =
    useState<AccessControlGroupResponse | null>(null);
  const [selectingAccessControlGroup, setSelectingAccessControlGroup] =
    useState(true);

  const addUserForTenant = (tenant: TenantResponse) => {
    setSelectedTenant(tenant);
    showAddUserModal();
  };

  const addBackendUserForTenant = (tenant: TenantResponse) => {
    setSelectedTenant(tenant);
    showAddBackendUserModal();
  };

  const setAddAccessControlGroupToTenant = (tenant: TenantResponse) => {
    setShowingModal("accessControlGroup");
    setSelectedTenant(tenant);
  };

  const addAccountForTenant = (tenant: TenantResponse) => {
    setSelectedTenant(tenant);
    showAddAccountModal();
  };

  const inviteUserToTenant = async (
    e: any,
    { isBackend } = { isBackend: false },
  ) => {
    e.preventDefault();
    setInProgress(true);
    try {
      const json = isBackend
        ? await props.addBackendUserToTenant({
            tenantExternalId:
              selectedTenant && selectedTenant.externalId
                ? selectedTenant.externalId
                : "",
            backendNameClaimId:
              selectedUser && selectedUser.externalId
                ? selectedUser.externalId
                : "",
          })
        : await props.addUserToTenant({
            tenantId: selectedTenant ? selectedTenant.id || "" : "",
            role,
            userId: selectedUser ? selectedUser.id || "" : "",
          });
      await props.requestTenants();
      setRole("");
      enqueueSnackbar(
        <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
          Successfully added user to tenant
        </p>,
        { variant: "success" },
      );
    } catch (err) {
      let errorMessage = "Error adding user to tenant";
      try {
        const e: any = err;
        if (e?.response) {
          const data =
            typeof e.response === "string"
              ? JSON.parse(e.response)
              : e.response;
          if (data?.errors?.length) {
            errorMessage = data.errors.map((x: any) => x.message).join(", ");
          } else if (data?.message) {
            errorMessage = data.message;
          }
        } else if (e?.message) {
          errorMessage = e.message;
        }
      } catch {
        // ignore parse errors
      }
      enqueueSnackbar(
        <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
          {errorMessage}
        </p>,
        { variant: "error" },
      );
    } finally {
      setInProgress(false);
    }
  };

  const addAccessControlGroupToTenant = async (e: any) => {
    e.preventDefault();
    setInProgress(true);
    try {
      const json = await props.addAccessControlGroupToTenant({
        tenantId: selectedTenant ? selectedTenant.id || "" : "",
        accessControlGroupId: selectedAccessControlGroup
          ? selectedAccessControlGroup.id || ""
          : "",
      });
      await props.requestTenants();
      setRole("");
      enqueueSnackbar(
        <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
          Successfully added group to tenant
        </p>,
        { variant: "success" },
      );
    } catch (err) {
      let errorMessage = "Error adding group to tenant";
      try {
        const e: any = err;
        if (e?.response) {
          const data =
            typeof e.response === "string"
              ? JSON.parse(e.response)
              : e.response;
          if (data?.errors?.length) {
            errorMessage = data.errors.map((x: any) => x.message).join(", ");
          } else if (data?.message) {
            errorMessage = data.message;
          }
        } else if (e?.message) {
          errorMessage = e.message;
        }
      } catch {
        // ignore parse errors
      }
      enqueueSnackbar(
        <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
          {errorMessage}
        </p>,
        { variant: "error" },
      );
    } finally {
      setInProgress(false);
    }
  };

  const showAddTenantModal = () => setShowingModal("tenant");
  const showAddUserModal = () => setShowingModal("user");
  const showAddBackendUserModal = () => setShowingModal("backendUser");
  const showAddAccountModal = () => setShowingModal("account");
  const closeModal = () => setShowingModal(null);

  const onRegisteredAccount = async () => {
    closeModal();
  };

  const onRegisteredTenant = async () => {
    closeModal();
    props.requestTenants();
  };

  const renderRegisterTenant = () => (
    // @ts-ignore
    <RegisterTenant onRegistered={onRegisteredTenant} />
  );

  const renderRegisterAccount = () => (
    <RegisterAccount
      // @ts-ignore
      ownerClientId={selectedTenant ? selectedTenant.externalId : ""}
      onRegistered={onRegisteredAccount}
      systemTypes={props.systemTypes}
    />
  );

  const renderTenantButton = (tenant: TenantResponse) => (
    <div className="grid grid-cols-2 gap-2 w-full">
      <Button
        className="w-full"
        size="sm"
        onClick={() => addUserForTenant(tenant)}
        type="button"
      >
        Add User
      </Button>
      <Button
        className="w-full"
        size="sm"
        onClick={() => addBackendUserForTenant(tenant)}
        type="button"
      >
        Add Backend User
      </Button>
      <Button
        className="w-full col-span-2 md:col-span-1"
        size="sm"
        onClick={() => setAddAccessControlGroupToTenant(tenant)}
        type="button"
      >
        Add Access Control Group
      </Button>
      <Button
        className="w-full col-span-2 md:col-span-1"
        size="sm"
        onClick={() => addAccountForTenant(tenant)}
        type="button"
      >
        Add Account
      </Button>
    </div>
  );

  const renderTenantsTable = () => (
    // @ts-ignore
    <SelectTenant pageSize={5} renderButton={renderTenantButton} />
  );

  const renderSelectUserButton = (clientUser: ConnectUserResponse) => (
    <div>
      <Button
        onClick={() => {
          setSelectingUser(false);
          setSelectedUser(clientUser);
        }}
        type="button"
      >
        Select
      </Button>
      <p />
    </div>
  );

  const renderSelectAccessControlGroupButton = (
    accessControlGroup: AccessControlGroupResponse,
  ) => (
    <div>
      <Button
        onClick={() => {
          setSelectingAccessControlGroup(false);
          setSelectedAccessControlGroup(accessControlGroup);
        }}
        type="button"
      >
        Select
      </Button>
      <p />
    </div>
  );

  const renderAddUserToTenant = ({ isBackend } = { isBackend: false }) => (
    <div>
      <form>
        <div className="form-group">
          <label htmlFor="clientUserId">User</label>
          <input
            className="form-control"
            name="clientUserId"
            placeholder="user id"
            value={selectedUser ? selectedUser.name : "please select a user"}
            disabled
            style={{ fontSize: 23 }}
            onChange={() => {}}
          />
        </div>
        {!isBackend && (
          <div className="form-group">
            <label htmlFor="role">Role</label>
            <input
              className="form-control"
              name="role"
              placeholder="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
            <p className="help-block">user or admin</p>
          </div>
        )}
        <div className="form-group">
          <label htmlFor="tennatId">Tenant Id</label>
          <input
            className="form-control"
            name="tenant id"
            placeholder="tenantId"
            disabled
            value={selectedTenant ? selectedTenant.id : ""}
            onChange={() => {}}
          />
        </div>
        <div>
          <Button
            type="submit"
            disabled={inProgress}
            onClick={(e) => inviteUserToTenant(e, { isBackend })}
          >
            {isBackend ? "Add API User" : "Add User"}
          </Button>
        </div>
      </form>
    </div>
  );

  const renderRegisterUser = ({ isBackend } = { isBackend: false }) => {
    if (!selectedTenant) return null;
    return renderAddUserToTenant({ isBackend });
  };

  const renderRegisterUserWrapper = ({ isBackend } = { isBackend: false }) => (
    <div>
      {(!selectedUser || selectingUser) && (
        <>
          <h3>Please Select a User</h3>
          {
            // @ts-ignore
            <SelectClientUser
              pageSize={5}
              renderButton={renderSelectUserButton}
            />
          }
        </>
      )}
      {!selectingUser && (
        <>
          <Button
            onClick={() => {
              setSelectingUser(true);
              setSelectedUser(null);
            }}
            type="button"
          >
            Change User
          </Button>
          {renderRegisterUser({ isBackend })}
        </>
      )}
    </div>
  );

  const renderAddAccessControlGroup = () => {
    if (!selectedTenant) return null;
    return (
      <div>
        <form>
          <div className="form-group">
            <label htmlFor="acgId">Access Control Group</label>
            <input
              className="form-control"
              name="acgId"
              placeholder="acg id"
              value={
                selectedAccessControlGroup
                  ? selectedAccessControlGroup.name
                  : "please select a group"
              }
              disabled
              style={{ fontSize: 23 }}
              onChange={() => {}}
            />
          </div>
          <div className="form-group">
            <label htmlFor="tennatId">Tenant Id</label>
            <input
              className="form-control"
              name="tenant id"
              placeholder="tenantId"
              disabled
              value={selectedTenant ? selectedTenant.id : ""}
              onChange={() => {}}
            />
          </div>
          <div>
            <Button
              type="submit"
              disabled={inProgress}
              onClick={addAccessControlGroupToTenant}
            >
              {"Add Access Control Group To Tenant"}
            </Button>
          </div>
        </form>
      </div>
    );
  };

  const renderAddAccessControlGroupWrapper = () => (
    <div>
      {(!selectedAccessControlGroup || selectingAccessControlGroup) && (
        <>
          <h3>Please Select a Group</h3>
          {
            // @ts-ignore
            <SelectAccessControlGroup
              pageSize={5}
              renderButton={renderSelectAccessControlGroupButton}
            />
          }
        </>
      )}
      {!selectingAccessControlGroup && (
        <>
          <Button
            onClick={() => {
              setSelectingAccessControlGroup(true);
              setSelectedAccessControlGroup(null);
            }}
            type="button"
          >
            Change Group
          </Button>
          {renderAddAccessControlGroup()}
        </>
      )}
    </div>
  );

  return (
    <main className="space-y-6">
      <header className="pb-5 border-b border-slate-200 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7 text-slate-900">
            Tenants
          </h1>
        </div>
        <Button
          className="mt-4 sm:mt-0"
          data-testid="tenants-button-create"
          type="button"
          onClick={showAddTenantModal}
        >
          Add Tenant
        </Button>
      </header>

      {renderTenantsTable()}
      <Modal
        show={!!showingModal}
        onHide={closeModal}
        aria-labelledby="ModalHeader"
        dialogClassName="custom-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title id="ModalHeader">
            {showingModal === "tenant"
              ? "Add Tenant"
              : showingModal === "user"
                ? "Add User to Tenant"
                : showingModal === "backendUser"
                  ? "Add API User to Tenant"
                  : showingModal === "accessControlGroup"
                    ? "Add Access Control Group to Tenant"
                    : showingModal === "account"
                      ? "Add Account to Tenant"
                      : "?"}
            {selectedTenant && <div>{selectedTenant.name}</div>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showingModal === "tenant"
            ? renderRegisterTenant()
            : showingModal === "user"
              ? renderRegisterUserWrapper()
              : showingModal === "backendUser"
                ? renderRegisterUserWrapper({ isBackend: true })
                : showingModal === "accessControlGroup"
                  ? renderAddAccessControlGroupWrapper()
                  : renderRegisterAccount()}
        </Modal.Body>
      </Modal>
    </main>
  );
};

export default connect<any, any, OwnProps, ApplicationState>(
  (state) => ({
    sites: state.sites,
  }),
  {
    ...TenantsState.actionCreators,
  },
)(AdminTenants);

type RegisterOwnProps = { ownerClientId: string; onRegistered: () => any };

interface RegisterInjected {}
type RegisterProps = RegisterInjected &
  typeof TenantsState.actionCreators &
  RegisterOwnProps;

interface RegisterState {
  inProgress: boolean;

  //create tenant
  ownerClientId: string; //aka ownerClientId/clientId
}

const RegisterTenantInner: React.FC<RegisterProps> = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [ownerClientId, setOwnerClientId] = useState("");
  const [inProgress, setInProgress] = useState(false);

  const registerTenant = async (e: any) => {
    e.preventDefault();
    setInProgress(true);
    try {
      const json = (await props.registerTenant({
        ownerClientId: ownerClientId,
      })) as any;
      enqueueSnackbar(
        <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
          Successfully registered tenant: {json?.ownerClientId || json?.id}
        </p>,
        { variant: "success" },
      );
      props.onRegistered();
      setOwnerClientId("");
    } catch (err) {
      let errorMessage = "Error registering tenant";
      try {
        const e: any = err;
        if (e?.response) {
          const data =
            typeof e.response === "string"
              ? JSON.parse(e.response)
              : e.response;
          if (data?.errors?.length) {
            errorMessage = data.errors.map((x: any) => x.message).join(", ");
          } else if (data?.message) {
            errorMessage = data.message;
          }
        } else if (e?.message) {
          errorMessage = e.message;
        }
      } catch {
        // ignore parse errors
      }
      enqueueSnackbar(
        <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
          {errorMessage}
        </p>,
        { variant: "error" },
      );
    } finally {
      setInProgress(false);
    }
  };

  return (
    <form>
      <div className="form-group">
        <label htmlFor="ownerClientId">Tenant Unique Name *</label>
        <input
          className="form-control"
          name="ownerClientId"
          placeholder="Tenant Unique Name"
          value={ownerClientId}
          onChange={(e) => setOwnerClientId(e.target.value)}
        />
        <p className="help-block">
          Enter any unique id you want, e.g. "Flats", "Property X"
          <br />
          For internal Livly accounts, tenant can be <b>Livly</b>
        </p>
      </div>
      <div>
        <Button
          type="submit"
          disabled={!!inProgress}
          onClick={registerTenant}
        >
          Register Tenant
        </Button>
      </div>
    </form>
  );
};

const RegisterTenant = connect<any, any, RegisterOwnProps, ApplicationState>(
  () => ({}),
  { ...TenantsState.actionCreators },
)(RegisterTenantInner);
